import { useState } from "react";
import type { Scenario } from "@radar-sim/schema";
import { isJoinCodeShape } from "@radar-sim/protocol";
import {
  Button,
  Card,
  Heading,
  Input,
  Label,
  Pill,
  Select,
  Stack,
} from "../ui/primitives.js";
import {
  DEFAULT_API,
  createAssessmentSession,
  createSession,
  joinAssessmentSession,
  joinSession,
} from "./api.js";

const ASSESSMENT_TAG = "g34.assessment.composite";
export function isAssessmentScenario(s: Scenario): boolean {
  return s.metadata.syllabusTags.includes(ASSESSMENT_TAG);
}

export interface SessionLobbyProps {
  email: string;
  scenarios: ReadonlyArray<Scenario>;
  onSession: (info: {
    sessionId: string;
    role: "instructor" | "student";
    scenarioId: string;
  }) => void;
  onAssessment: (info: {
    assessmentId: string;
    scenarioId: string;
    role: "instructor" | "student";
    instanceSessionId?: string;
  }) => void;
}

export function SessionLobby(props: SessionLobbyProps): JSX.Element {
  const studentVisible = props.scenarios.filter((s) => !isAssessmentScenario(s));
  const assessmentScenarios = props.scenarios.filter(isAssessmentScenario);

  const [scenarioId, setScenarioId] = useState<string>(
    studentVisible[0]?.metadata.id ?? props.scenarios[0]?.metadata.id ?? "",
  );
  const [assessmentScenarioId, setAssessmentScenarioId] = useState<string>(
    assessmentScenarios[0]?.metadata.id ?? "",
  );
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    joinCode: string;
    sessionId: string;
  } | null>(null);
  const [assessmentCreated, setAssessmentCreated] = useState<{
    joinCode: string;
    assessmentId: string;
  } | null>(null);

  const scenario = props.scenarios.find((s) => s.metadata.id === scenarioId);
  const assessmentScenario = props.scenarios.find(
    (s) => s.metadata.id === assessmentScenarioId,
  );

  const createInstructor = async () => {
    if (scenario === undefined) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createSession(DEFAULT_API, scenario);
      setCreated(res);
      props.onSession({
        sessionId: res.sessionId,
        role: "instructor",
        scenarioId: scenario.metadata.id,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const startAssessment = async () => {
    if (assessmentScenario === undefined) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createAssessmentSession(DEFAULT_API, assessmentScenario);
      setAssessmentCreated(res);
      props.onAssessment({
        assessmentId: res.assessmentId,
        scenarioId: assessmentScenario.metadata.id,
        role: "instructor",
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const joinAny = async () => {
    const trimmed = joinCodeInput.trim().toUpperCase();
    if (!isJoinCodeShape(trimmed)) {
      setError("Join code must be 6 uppercase characters.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await joinAssessmentSession(DEFAULT_API, trimmed);
      props.onAssessment({
        assessmentId: res.assessmentId,
        scenarioId: res.scenarioId,
        role: "student",
        instanceSessionId: res.instanceSessionId,
      });
      return;
    } catch {
      // fall through to classroom
    }
    try {
      const res = await joinSession(DEFAULT_API, trimmed);
      props.onSession({
        sessionId: res.sessionId,
        role: res.role,
        scenarioId: res.scenarioId,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: "100%", maxWidth: 980 }}>
      <Stack gap={24}>
        <Stack gap={4}>
          <Heading level={1} style={{ fontSize: 24 }}>
            Sessions
          </Heading>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14 }}>
            Signed in as <strong style={{ color: "var(--text)" }}>{props.email}</strong>.
          </p>
        </Stack>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
            gap: 16,
          }}
        >
          {/* Instructor — classroom */}
          <Card>
            <Stack gap={14}>
              <Stack gap={4}>
                <Pill tone="accent">Instructor</Pill>
                <Heading level={2}>Start a classroom session</Heading>
                <p style={muted}>
                  Pick a scenario and create a session; students join with the
                  6-character code.
                </p>
              </Stack>
              <Stack gap={6}>
                <Label>Scenario</Label>
                <Select
                  value={scenarioId}
                  onChange={setScenarioId}
                  options={studentVisible.map((s) => ({
                    value: s.metadata.id,
                    label: s.metadata.name,
                  }))}
                />
              </Stack>
              <Button onClick={createInstructor} disabled={busy} fullWidth>
                {busy ? "Creating…" : "Create classroom session"}
              </Button>
              {created !== null && (
                <CodeBox
                  label="Classroom join code"
                  code={created.joinCode}
                  meta={`Session id: ${created.sessionId}`}
                />
              )}
            </Stack>
          </Card>

          {/* Instructor — assessment */}
          {assessmentScenarios.length > 0 && (
            <Card>
              <Stack gap={14}>
                <Stack gap={4}>
                  <Pill tone="success">Instructor</Pill>
                  <Heading level={2}>Start an assessment</Heading>
                  <p style={muted}>
                    Each student gets a forked instance. Surface the dashboard
                    tile and inject events per student.
                  </p>
                </Stack>
                <Stack gap={6}>
                  <Label>Assessment script</Label>
                  <Select
                    value={assessmentScenarioId}
                    onChange={setAssessmentScenarioId}
                    options={assessmentScenarios.map((s) => ({
                      value: s.metadata.id,
                      label: s.metadata.name,
                    }))}
                  />
                </Stack>
                <Button onClick={startAssessment} disabled={busy} fullWidth>
                  {busy ? "Starting…" : "Start assessment"}
                </Button>
                {assessmentCreated !== null && (
                  <CodeBox
                    label="Assessment join code"
                    code={assessmentCreated.joinCode}
                    meta={`Assessment id: ${assessmentCreated.assessmentId}`}
                  />
                )}
              </Stack>
            </Card>
          )}

          {/* Student — join */}
          <Card>
            <Stack gap={14}>
              <Stack gap={4}>
                <Pill>Student</Pill>
                <Heading level={2}>Join a session</Heading>
                <p style={muted}>
                  Enter the 6-character code from your instructor.
                </p>
              </Stack>
              <Stack gap={6}>
                <Label>Join code</Label>
                <Input
                  value={joinCodeInput}
                  onChange={(v) => setJoinCodeInput(v.toUpperCase())}
                  ariaLabel="Join code"
                  maxLength={6}
                  style={{
                    textAlign: "center",
                    letterSpacing: 4,
                    fontSize: 22,
                    fontWeight: 600,
                    fontFamily: "var(--font-mono)",
                  }}
                />
              </Stack>
              <Button
                onClick={joinAny}
                disabled={busy || joinCodeInput.length === 0}
                fullWidth
              >
                {busy ? "Joining…" : "Join session"}
              </Button>
            </Stack>
          </Card>
        </div>

        {error !== null && (
          <p
            role="alert"
            style={{
              margin: 0,
              color: "var(--danger)",
              fontSize: 13,
            }}
          >
            {error}
          </p>
        )}
      </Stack>
    </div>
  );
}

const muted: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: "var(--text-muted)",
};

function CodeBox(props: {
  label: string;
  code: string;
  meta: string;
}): JSX.Element {
  return (
    <div
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        padding: 12,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>
        {props.label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: 4,
          color: "var(--accent)",
        }}
      >
        {props.code}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 6 }}>
        {props.meta}
      </div>
    </div>
  );
}
